import FW from '../fw/fw';
import Env from '../fw/env';
import Logger from '../fw/logger';


export default class ThreeSixty {

  #rmp;
  #requestAnimation360 = null;
  #texture360 = null;
  #scene360 = null;
  #mesh360 = null;
  #canvas360 = null;
  #renderer360 = null;
  #camera360 = null;
  #dragStart360 = {};
  #mouseDown360 = false;
  #moveInProgress360 = false;
  #lat = 0;
  #lon = 0;
  #fov = 65;
  #progressiveInterval360 = null;
  #UI360InteractionFn = null;
  #setCamLatLongFovTopFn = null;
  #setCamLatLongFovBottomFn = null;
  #setCamLatLongFovLeftFn = null;
  #setCamLatLongFovRightFn = null;
  #setCamLatLongFovMinusFn = null;
  #setCamLatLongFovResetFn = null;
  #setCamLatLongFovPlusFn = null;

  constructor(rmp) {
    this.#rmp = rmp;
  }

  get fov() {
    return this.#fov;
  }

  get lon() {
    return this.#lon;
  }

  get lat() {
    return this.#lat;
  }

  #reset360Vars() {
    this.#requestAnimation360 = null;
    this.#texture360 = null;
    this.#scene360 = null;
    this.#mesh360 = null;
    this.#canvas360 = null;
    this.#renderer360 = null;
    this.#camera360 = null;
    this.#dragStart360 = {};
    this.#mouseDown360 = false;
    this.#moveInProgress360 = false;
    this.#lat = 0;
    this.#lon = 0;
    this.#fov = 65;
    this.#progressiveInterval360 = null;
    this.#UI360InteractionFn = null;
    this.#setCamLatLongFovTopFn = null;
    this.#setCamLatLongFovBottomFn = null;
    this.#setCamLatLongFovLeftFn = null;
    this.#setCamLatLongFovRightFn = null;
    this.#setCamLatLongFovMinusFn = null;
    this.#setCamLatLongFovResetFn = null;
    this.#setCamLatLongFovPlusFn = null;
  }

  #tsOnMouseDownIos(event) {
    if (event) {
      // touch-action: none; does not work on iOS
      try {
        event.preventDefault();
      } catch (warning) {
        console.warn(warning);
      }
      this.#dragStart360.x = FW.getPosXTouch(event);
      this.#dragStart360.y = FW.getPosYTouch(event);
      this.#canvas360.classList.add('grabbing');
      this.#mouseDown360 = true;
    }
  }

  #tsOnMouseDown(event) {
    if (event) {
      if (event.type === 'mousedown') {
        this.#dragStart360.x = FW.getPosX(event);
        this.#dragStart360.y = FW.getPosY(event);
      } else if (event.type === 'touchstart') {
        this.#dragStart360.x = FW.getPosXTouch(event);
        this.#dragStart360.y = FW.getPosYTouch(event);
      }
      this.#canvas360.classList.add('grabbing');
      this.#mouseDown360 = true;
    }
  }

  #tsOnMouseUp() {
    this.#canvas360.classList.remove('grabbing');
    this.#mouseDown360 = false;
  }

  #tsOnMouseMove(event) {
    if (this.#moveInProgress360 || !this.#mouseDown360) {
      Logger.print('360', 'progressive move in progress - camera may not be updated right now');
      return;
    }
    let x, y, posX, posY;
    if (event.type === 'mousemove') {
      posX = FW.getPosX(event);
      posY = FW.getPosY(event);
    } else if (event.type === 'touchmove') {
      posX = FW.getPosXTouch(event);
      posY = FW.getPosYTouch(event);
    }
    x = this.#dragStart360.x - posX;
    y = this.#dragStart360.y - posY;
    this.#dragStart360.x = posX;
    this.#dragStart360.y = posY;
    const playerSize = this.#rmp.getPlayerSize();
    if (playerSize.width > 0 && playerSize.height > 0) {
      const ratio = playerSize.width / playerSize.height;
      let playerSizeFactor = 1;
      if (this.#rmp.dom.container.classList.contains('rmp-sm')) {
        playerSizeFactor = 2;
      } else if (this.#rmp.dom.container.classList.contains('rmp-md')) {
        playerSizeFactor = 1.5;
      }
      const mouseMoveSpeedFactor = 0.1;
      this.#lon += x * mouseMoveSpeedFactor * playerSizeFactor;
      this.#lat += y * mouseMoveSpeedFactor * ratio * playerSizeFactor;
    }
  }

  #setFocalLength() {
    if (this.#camera360) {
      if (this.#fov > this.#rmp.video360MaxFocalLength) {
        this.#fov = this.#rmp.video360MaxFocalLength;
      } else if (this.#fov < this.#rmp.video360MinFocalLength) {
        this.#fov = this.#rmp.video360MinFocalLength;
      }
      Logger.print('360', 'setFocalLength to ' + this.#fov);
      this.#camera360.fov = this.#fov;
      this.#camera360.updateProjectionMatrix();
    }
  }

  #tsOnWheel(event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (this.#moveInProgress360) {
      Logger.print('360', 'progressive move in progress - camera may not be updated right now');
      return;
    }
    const fovRangeFactor = 10;
    if (event && event.wheelDeltaY) {
      if (event.wheelDeltaY > 0) {
        this.#fov -= fovRangeFactor;
      } else {
        this.#fov += fovRangeFactor;
      }
      this.#setFocalLength();
    }
  }

  #animate360() {
    // set our animate function to fire next time a frame is ready
    try {
      this.#requestAnimation360 = requestAnimationFrame(this.#animate360.bind(this));
    } catch (warning) {
      console.warn(warning);
    }
    const viewingDistance = 50;
    this.#lat = Math.max(-85, Math.min(85, this.#lat));
    const MathUtils = window.THREE.MathUtils;
    const phi = MathUtils.degToRad(90 - this.#lat);
    const theta = MathUtils.degToRad(this.#lon);
    const partialMath = viewingDistance * Math.sin(phi);
    this.#camera360.position.x = partialMath * Math.cos(theta);
    this.#camera360.position.y = viewingDistance * Math.cos(phi);
    this.#camera360.position.z = partialMath * Math.sin(theta);
    this.#camera360.lookAt(this.#camera360.target);
    this.#renderer360.render(this.#scene360, this.#camera360);
  }

  #wire360UI() {
    // attach drag event
    if (!this.#rmp.isMobile) {
      this.#rmp.dom.content.addEventListener('mousedown', this.#tsOnMouseDown.bind(this));
      this.#rmp.dom.content.addEventListener('mouseup', this.#tsOnMouseUp.bind(this));
      this.#rmp.dom.content.addEventListener('mouseleave', this.#tsOnMouseUp.bind(this));
      this.#rmp.dom.content.addEventListener('mousemove', this.#tsOnMouseMove.bind(this));
      this.#rmp.dom.content.addEventListener('wheel', this.#tsOnWheel.bind(this));
    } else {
      if (Env.isIos[0]) {
        this.#rmp.dom.content.addEventListener('touchstart', this.#tsOnMouseDownIos.bind(this));
      } else {
        this.#rmp.dom.content.addEventListener('touchstart', this.#tsOnMouseDown.bind(this), Env.hasPassiveEventListeners ? { passive: true } : false);
      }
      this.#rmp.dom.content.addEventListener('touchend', this.#tsOnMouseUp.bind(this));
      this.#rmp.dom.content.addEventListener('touchmove', this.#tsOnMouseMove.bind(this), Env.hasPassiveEventListeners ? { passive: true } : false);
    }

    // show 360 UI
    FW.show(this.#rmp.dom.ui360);

    const arrowRangeFactor = 20;
    const fovRangeFactor = 10;
    const uiTop360 = FW.createElement(
      'button',
      'rmp-button rmp-360-nav-button rmp-360-nav-top rmp-i rmp-i-up',
      null,
      null
    );
    this.#setCamLatLongFovTopFn = this.setCamLatLongFov360.bind(this, arrowRangeFactor, 0, 0, false);
    FW.addEvents(['touchend', 'click'], uiTop360, this.#setCamLatLongFovTopFn);
    this.#rmp.dom.ui360.appendChild(uiTop360);

    const uiBottom360 = FW.createElement(
      'button',
      'rmp-button rmp-360-nav-button rmp-360-nav-bottom rmp-i rmp-i-down',
      null,
      null
    );
    this.#setCamLatLongFovBottomFn = this.setCamLatLongFov360.bind(this, -arrowRangeFactor, 0, 0, false);
    FW.addEvents(['touchend', 'click'], uiBottom360, this.#setCamLatLongFovBottomFn);
    this.#rmp.dom.ui360.appendChild(uiBottom360);

    const uiLeft360 = FW.createElement(
      'button',
      'rmp-button rmp-360-nav-button rmp-360-nav-left rmp-i rmp-i-left',
      null,
      null
    );
    this.#setCamLatLongFovLeftFn = this.setCamLatLongFov360.bind(this, 0, -arrowRangeFactor, 0, false);
    FW.addEvents(['touchend', 'click'], uiLeft360, this.#setCamLatLongFovLeftFn);
    this.#rmp.dom.ui360.appendChild(uiLeft360);

    const uiRight360 = FW.createElement(
      'button',
      'rmp-button rmp-360-nav-button rmp-360-nav-right rmp-i rmp-i-right',
      null,
      null
    );
    this.#setCamLatLongFovRightFn = this.setCamLatLongFov360.bind(this, 0, arrowRangeFactor, 0, false);
    FW.addEvents(['touchend', 'click'], uiRight360, this.#setCamLatLongFovRightFn);
    this.#rmp.dom.ui360.appendChild(uiRight360);

    const uiMinus360 = FW.createElement(
      'button',
      'rmp-button rmp-360-nav-button rmp-360-nav-minus rmp-i rmp-i-minus-360',
      null,
      null
    );
    this.#setCamLatLongFovMinusFn = this.setCamLatLongFov360.bind(this, 0, 0, -fovRangeFactor, false);
    FW.addEvents(['touchend', 'click'], uiMinus360, this.#setCamLatLongFovMinusFn);
    this.#rmp.dom.ui360.appendChild(uiMinus360);

    const uiReset360 = FW.createElement(
      'button',
      'rmp-button rmp-360-nav-button rmp-360-nav-reset rmp-i rmp-i-reset-360',
      null,
      null
    );
    this.#setCamLatLongFovResetFn = this.setCamLatLongFov360.bind(this,
      this.#rmp.video360InitialLat,
      this.#rmp.video360InitialLon,
      this.#rmp.video360FocalLength,
      true);
    FW.addEvents(['touchend', 'click'], uiReset360, this.#setCamLatLongFovResetFn);
    this.#rmp.dom.ui360.appendChild(uiReset360);

    const uiPlus360 = FW.createElement(
      'button',
      'rmp-button rmp-360-nav-button rmp-360-nav-plus rmp-i rmp-i-plus-360',
      null,
      null
    );
    this.#setCamLatLongFovPlusFn = this.setCamLatLongFov360.bind(this, 0, 0, fovRangeFactor, false);
    FW.addEvents(['touchend', 'click'], uiPlus360, this.#setCamLatLongFovPlusFn);
    this.#rmp.dom.ui360.appendChild(uiPlus360);

    const hintLabel = this.#rmp.labels.hint;

    this.#rmp.rmpAccessible.button(uiTop360, '360 ' + hintLabel.up, true);
    this.#rmp.rmpAccessible.button(uiBottom360, '360 ' + hintLabel.down, true);
    this.#rmp.rmpAccessible.button(uiLeft360, '360 ' + hintLabel.left, true);
    this.#rmp.rmpAccessible.button(uiRight360, '360 ' + hintLabel.right, true);
    this.#rmp.rmpAccessible.button(uiMinus360, '360 ' + hintLabel.minus, true);
    this.#rmp.rmpAccessible.button(uiReset360, '360 ' + hintLabel.default, true);
    this.#rmp.rmpAccessible.button(uiPlus360, '360 ' + hintLabel.plus, true);
  }

  #onPlayingUpdate360UI() {
    Logger.print('360', 'playing event reached - append and wire 360 UI');
    // attach 360 arrow UI
    const uiText360 = this.#rmp.dom.container.querySelector('.rmp-360-text');
    if (uiText360) {
      FW.removeElement(uiText360);
    }
    if (this.#rmp.isMobile) {
      return;
    }
    this.#wire360UI();
  }

  #UI360Interaction(event) {
    FW.eventStopPrevent(event);
    this.#rmp.rmpUIManagement.showChrome();
  }

  #handleOrientation(event) {
    const alpha = event.alpha;
    const gamma = event.gamma;
    const beta = event.beta;
    if (Env.orientation === 'landscape') {
      if (gamma < -85 || gamma > 85) {
        return;
      }
      this.#lon = -alpha;
      this.#lat = gamma + this.#rmp.video360MobileGyroOffset;
    } else {
      if (beta > 85 || beta < -85) {
        return;
      }
      this.#lon = -gamma;
      this.#lat = -beta + this.#rmp.video360MobileGyroOffset;
    }
  }

  #requestDeviceOrientationEventPermission(event) {
    FW.eventStopPrevent(event);
    DeviceOrientationEvent.requestPermission().
      then(permissionState => {
        Logger.print('360', `permission is ${permissionState}`);
        if (permissionState === 'granted') {
          window.addEventListener('deviceorientation', this.#handleOrientation.bind(this), true);
        } else {
          this.#prepare360UI();
          this.#wire360UI();
        }
      }).
      catch(warning => {
        console.warn(warning);
        this.#prepare360UI();
        this.#wire360UI();
      });
  }

  #prepareCanvas() {
    const playerSize = this.#rmp.getPlayerSize();
    let width = 640;
    if (playerSize.width > 0) {
      width = playerSize.width;
    }
    let height = 360;
    if (playerSize.height > 0) {
      height = playerSize.height;
    }
    const ratio = width / height;
    this.#lat = this.#rmp.video360InitialLat;
    this.#lon = this.#rmp.video360InitialLon;
    this.#fov = this.#rmp.video360FocalLength;
    // camera
    this.#camera360 = new window.THREE.PerspectiveCamera(this.#rmp.video360FocalLength, ratio, 1, 1100);
    this.#camera360.target = new window.THREE.Vector3(0, 0, 0);

    // scene
    this.#scene360 = new window.THREE.Scene();

    // geometry
    const segmentWidth = 60;
    const segmentHeight = 2 * Math.round((segmentWidth / (ratio)) / 2);
    const geometry = new window.THREE.SphereGeometry(500, segmentWidth, segmentHeight);
    // invert the geometry on the x-axis so that all of the faces point inward
    geometry.scale(-1, 1, 1);

    //texture
    this.#texture360 = new window.THREE.VideoTexture(this.#rmp.dom.video);
    const material = new window.THREE.MeshBasicMaterial({ map: this.#texture360 });

    // mesh
    this.#mesh360 = new window.THREE.Mesh(geometry, material);
    this.#scene360.add(this.#mesh360);

    // renderer
    this.#renderer360 = new window.THREE.WebGLRenderer();
    this.#renderer360.setPixelRatio(Env.devicePixelRatio);
    this.#renderer360.setSize(width, height);
    Logger.print('360', 'WebGLRenderer created - details follow', this.#renderer360);

    // append the rendering element to this div
    this.#canvas360 = this.#renderer360.domElement;
    this.#canvas360.className = 'rmp-canvas-360 rmp-grab';
    FW.insertBefore(this.#rmp.dom.content, this.#canvas360, this.#rmp.dom.poster);

    // hide HTML5 player
    FW.hide(this.#rmp.dom.video);
  }

  #prepare360UI() {
    this.#rmp.dom.ui360 = FW.createElement('div', 'rmp-360-nav rmp-color-bg');
    this.#UI360InteractionFn = this.#UI360Interaction.bind(this);
    FW.addEvents(['touchend', 'click'], this.#rmp.dom.ui360, this.#UI360InteractionFn);
    FW.hide(this.#rmp.dom.ui360);
    this.#rmp.dom.container.appendChild(this.#rmp.dom.ui360);
  }

  #onReadyMain360() {
    Logger.print('360', 'create three.js Scene, PerspectiveCamera and WebGLRenderer at ' +
      (FW.getNow() - this.#rmp.refTime) + ' ms');
    this.#rmp.one('playing', this.#onPlayingUpdate360UI.bind(this));

    // variables
    this.#prepareCanvas();

    if (this.#rmp.isMobile) {
      if ((Env.isIos[0] || Env.isIpadOS) && typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function') {
        Logger.print('360', 'requesting DeviceOrientationEvent permission for iOS|iPadOS on next touch');
        FW.addEventsOnce(['touchend', 'click'], this.#rmp.dom.centralMobileUI, this.#requestDeviceOrientationEventPermission.bind(this));
      } else {
        Logger.print('360', 'wire deviceorientation event for devices where permission is not needed');
        window.addEventListener('deviceorientation', this.#handleOrientation.bind(this), true);
      }
    } else {
      // 360 UI
      this.#prepare360UI();
      // for desktop only
      this.#rmp.dom.ui360.addEventListener('mouseenter', event => {
        if (event) {
          event.stopPropagation();
        }
        this.#rmp.forceResetTimer = true;
      });
      this.#rmp.dom.ui360.addEventListener('mouseleave', event => {
        if (event) {
          event.stopPropagation();
        }
        this.#rmp.forceResetTimer = false;
      });
    }

    // 360 text placeholder
    const uiText360 = FW.createElement('div', 'rmp-360-text rmp-color-bg', '360°', this.#rmp.rtl);
    this.#rmp.dom.container.appendChild(uiText360);

    // animate
    this.#animate360();

  }

  #run360() {
    Logger.print('360', 'three.js available at ' + (FW.getNow() - this.#rmp.refTime) + ' ms');
    if (this.#rmp.playerReady) {
      this.#onReadyMain360();
    } else {
      this.#rmp.one('ready', this.#onReadyMain360.bind(this));
    }
  }

  resize360() {
    if (this.#renderer360 && this.#camera360) {
      let width = -1;
      let height = -1;
      if (this.#rmp.isInFullscreen) {
        if (this.#rmp.hasNativeFullscreenSupport) {
          width = Env.screenDimension.width;
          height = Env.screenDimension.height;
        } else if (this.#rmp.fullscreenFullWindowMode) {
          if (Env.viewportSize.width > -1 && Env.viewportSize.height > -1) {
            width = Env.viewportSize.width;
            height = Env.viewportSize.height;
          }
        }
      } else {
        const playerSize = this.#rmp.getPlayerSize();
        width = playerSize.width;
        height = playerSize.height;
      }
      if (width > 0 && height > 0) {
        this.#renderer360.setSize(width, height);
        this.#camera360.aspect = width / height;
        this.#camera360.updateProjectionMatrix();
      }
    }
  }

  setCamLatLongFov360(lat, lon, fov, api, event) {
    if (this.#moveInProgress360) {
      Logger.print('360', 'cannot update camera - last move in progress');
      return;
    }
    this.#moveInProgress360 = true;
    FW.eventStopPrevent(event);
    if (api) {
      this.#lon = lon;
      this.#lat = lat;
      this.#fov = fov;
      this.#setFocalLength();
      this.#moveInProgress360 = false;
      return;
    } else {
      this.#rmp.rmpUIManagement.showChrome();
    }
    const arrowMoveSteps = 20;
    if (lon !== 0) {
      this.#lon += lon / arrowMoveSteps;
    }
    if (lat !== 0) {
      this.#lat -= lat / arrowMoveSteps;
    }
    if (fov !== 0) {
      this.#fov -= fov / arrowMoveSteps;
      this.#setFocalLength();
    }
    let currentStep = 1;
    window.clearInterval(this.#progressiveInterval360);
    this.#progressiveInterval360 = window.setInterval(() => {
      if (lon !== 0) {
        this.#lon += lon / arrowMoveSteps;
      }
      if (lat !== 0) {
        this.#lat -= lat / arrowMoveSteps;
      }
      if (fov !== 0) {
        this.#fov -= fov / arrowMoveSteps;
        this.#setFocalLength();
      }
      currentStep++;
      if (currentStep === arrowMoveSteps) {
        this.#moveInProgress360 = false;
        window.clearInterval(this.#progressiveInterval360);
      }
    }, arrowMoveSteps);
  }

  destroy() {
    try {
      if (this.#requestAnimation360) {
        cancelAnimationFrame(this.#requestAnimation360);
      }
      if (this.#texture360) {
        this.#texture360.dispose();
      }
      if (this.#scene360 && this.#mesh360) {
        this.#scene360.remove(this.#mesh360);
      }
    } catch (warning) {
      console.warn(warning);
    }
    window.clearInterval(this.#progressiveInterval360);
    FW.removeElement(this.#canvas360);
    this.#reset360Vars();
  }

  init() {
    if (typeof window.THREE !== 'undefined') {
      this.#run360();
    } else {
      // do not change to const pathToRmpFiles = this.#rmp.pathToRmpFiles or grunt replace won't work
      let callScript = this.#rmp.pathToRmpFiles + 'three/three.min.js';
      if (DEBUG) {
        callScript = this.#rmp.pathToRmpFiles + 'three/three.js';
      }
      Logger.print('360', 'starts loading three.js lib at ' + (FW.getNow() - this.#rmp.refTime) + ' ms');
      FW.getScript(
        callScript,
        this.#run360.bind(this),
        this.#rmp.rmpUtils.error.bind(this.#rmp.rmpUtils, 500),
        this.#rmp.shadowRoot
      );
    }
  }

}
